import { ExclamationCircleOutlined } from '@ant-design/icons';
import { Button, Card, DatePicker, Form, InputNumber } from 'antd';
import confirm from 'antd/lib/modal/confirm';
import moment from 'moment';
import { useCallback, useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate, useParams } from 'react-router-dom';

import {
  createConsignmentOrder,
  getConsignmentOrderById,
  getProviders,
  updateConsignmentOrder,
} from '../../../../appRedux/actions';

import {
  buttonLabel,
  cardConfig,
  dateFormatMx,
  formConfig,
  messages,
  modalButtons,
  orderConfirmModal,
  placeHolders,
  UrlPaths,
} from '../../../../constants';

import TextArea from 'antd/lib/input/TextArea';
import { CancelButton, CustomSelect } from '../../../uiComponents';
import { ConsignmentOrderProducts } from '../ConsignmentOrderProducts';

export const ConsignmentOrderForm = () => {
  const { id } = useParams();
  const editMode = id;
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const [form] = Form.useForm();

  const [response, setResponse] = useState();
  const [formDisabled, setFormDisabled] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [providerId, setProviderId] = useState(null);

  const { providers } = useSelector((state) => state.provider);
  const { consignmentOrderById } = useSelector(
    (state) => state.consignmentOrder
  );

  useEffect(() => {
    if (editMode) dispatch(getConsignmentOrderById(id));
    dispatch(getProviders({ withDeleted: 'false' }));
  }, [dispatch, editMode, id]);

  useEffect(() => {
    if (response && [200, 201].includes(response))
      navigate(UrlPaths.CONSIGNMENT_ORDER);
  }, [response, navigate]);

  useEffect(() => {
    if (!editMode || !consignmentOrderById) return;

    const mappedProducts =
      consignmentOrderById.consignmentOrderProduct?.map((item) => ({
        ...item,
        expiresAt: item.expiresAt ? moment(item.expiresAt) : null,
        productId: {
          value: item.product.id,
          label: item.product.name,
        },
      })) ?? [];

    const values = {
      providerId: consignmentOrderById.provider?.id ?? null,
      arrivedAt: consignmentOrderById.arrivedAt
        ? moment(consignmentOrderById.arrivedAt)
        : null,
      approvedAt: consignmentOrderById.approvedAt
        ? moment(consignmentOrderById.approvedAt)
        : null,
      tax: consignmentOrderById.tax,
      amount: consignmentOrderById.amount,
      comments: consignmentOrderById.comments,
      consignmentOrderProduct: mappedProducts,
    };

    setProviderId(values.providerId);
    form.setFieldsValue(values);

    const isFinished = !!consignmentOrderById.approvedAt;
    setFormDisabled(isFinished);
  }, [editMode, consignmentOrderById, form]);

  useEffect(() => {
    if (consignmentOrderById && editMode) {
      if (consignmentOrderById.arrivedAt)
        consignmentOrderById.arrivedAt = moment(consignmentOrderById.arrivedAt);

      if (consignmentOrderById.approvedAt)
        consignmentOrderById.approvedAt = moment(
          consignmentOrderById.approvedAt
        );

      consignmentOrderById.consignmentOrderProduct?.forEach((item) => {
        if (item.expiresAt) item.expiresAt = moment(item.expiresAt);
        item.productId = {
          value: item.product.id,
          label: item.product.name,
        };
      });

      setProviderId(consignmentOrderById.provider?.id);
    }
  }, [form, editMode, consignmentOrderById]);

  const onProviderChange = useCallback(
    (value) => {
      setProviderId(value);
      form.setFieldsValue({ consignmentOrderProduct: [''] });
    },
    [form]
  );

  const showOrderConfirm = (values) => {
    confirm({
      title: orderConfirmModal.title,
      icon: <ExclamationCircleOutlined />,
      content: orderConfirmModal.body,
      okText: modalButtons.ok,
      cancelText: modalButtons.cancel,
      okButtonProps: { loading: isLoading },
      onOk() {
        setIsLoading(true);
        onSubmit(values);
      },
    });
  };

  const onSubmit = async (values) => {
    const products = values?.consignmentOrderProduct?.map((p) => ({
      ...p,
      productId: p.productId.value || p.productId,
    }));

    const newValues = {
      ...values,
      consignmentOrderProduct: products,
    };

    if (editMode) {
      setResponse(await dispatch(updateConsignmentOrder(id, newValues)));
    } else {
      setResponse(await dispatch(createConsignmentOrder(newValues)));
    }
  };

  return (
    <>
      <Card size="small" bordered style={cardConfig}>
        <Form
          id="onSubmitConsignment"
          initialValues={{ consignmentOrderProduct: [''] }}
          autoComplete="off"
          onFinish={showOrderConfirm}
          form={form}
          disabled={formDisabled}
        >
          {providers && (
            <Form.Item
              {...formConfig}
              name="providerId"
              label="Proveedor"
              rules={[
                { required: true, message: messages.required('Proveedor') },
              ]}
            >
              <CustomSelect
                placeholder={placeHolders.provider}
                onChange={onProviderChange}
                options={providers.map(({ id, name }) => ({
                  value: id,
                  label: name,
                }))}
                disabled={editMode}
              />
            </Form.Item>
          )}
          <Form.Item
            {...formConfig}
            name="arrivedAt"
            label="Fecha de llegada"
            rules={[
              {
                required: true,
                message: messages.required('Fecha de llegada'),
              },
            ]}
          >
            <DatePicker
              style={{ width: '100%' }}
              format={dateFormatMx}
              disabled={editMode}
            />
          </Form.Item>
          <Form.Item
            {...formConfig}
            name="tax"
            label="IVA"
            rules={[{ required: true, message: messages.required('IVA') }]}
          >
            <CustomSelect
              placeholder="IVA"
              options={[
                { label: '0%', value: 0 },
                { label: '8%', value: 8 },
                { label: '16%', value: 16 },
              ]}
              disabled={editMode}
            />
          </Form.Item>

          <Form.Item
            {...formConfig}
            name="amount"
            label="Total"
            rules={[{ required: true, message: messages.required('Total') }]}
          >
            <InputNumber
              formatter={(value) =>
                `${value}`.replace(/\B(?=(\d{3})+(?!\d))/g, ',')
              }
              parser={(value) => value?.replace(/(,*)/g, '')}
              stringMode={true}
            />
          </Form.Item>

          <Form.Item {...formConfig} name="comments" label="Comentarios">
            <TextArea disabled={editMode} />
          </Form.Item>

          <ConsignmentOrderProducts
            form={form}
            providerId={providerId}
            disabled={editMode}
          />

          <Button
            type="primary"
            htmlType="submit"
            className="btn-form"
            form="onSubmitConsignment"
          >
            {buttonLabel.save}
          </Button>
        </Form>

        <CancelButton path={UrlPaths.CONSIGNMENT_ORDER} />
      </Card>
    </>
  );
};
